package com.example.monetix.utils

import androidx.compose.runtime.mutableStateListOf
import kotlinx.datetime.Clock

data class PendingNotification(
    val id: Int,
    val amount: String,
    val merchant: String,
    val isIncome: Boolean
)

object BankNotificationCatcher {
    // 🚀 ORA È UNA LISTA REATTIVA DEL TIPO CORRETTO!
    val pendingQueue = mutableStateListOf<PendingNotification>()

    // Il canale di comunicazione verso il modulo Android
    var onNotificationConsumed: ((Int) -> Unit)? = null

    // Variabili di memoria per il filtro anti-doppioni
    private var lastProcessedHash: Int = 0
    private var lastProcessedTimeMs: Long = 0L

    val hasNewNotification: Boolean
        get() = pendingQueue.isNotEmpty()

    val currentNotification: PendingNotification?
        get() = pendingQueue.firstOrNull()

    fun catchNotification(id: Int, amount: String, merchant: String, isIncome: Boolean): Boolean {

        val combinedText = "$merchant $amount".lowercase()

        // 🚀 1. WHITELIST USCITE: Se è un prelievo, diamo il lasciapassare assoluto e forziamo uscita!
        val safeKeywords = listOf("prelievo", "atm", "bancomat", "sportello", "contante", "contanti")
        val isSafeExpense = safeKeywords.any { combinedText.contains(it) }

        // 🚀 1.5 WHITELIST ENTRATE: Se troviamo parole chiave di guadagno, diamo il lasciapassare e forziamo entrata!
        val incomeKeywords = listOf(
            "ricevuto", "accredito", "bonifico", "stipendio", "entrata",
            "rimborso", "storno", "incasso", "versamento"
        )
        val isSafeIncome = incomeKeywords.any { combinedText.contains(it) }

        // 2. BLACKLIST (Filtro anti-spam)
        val ignoredKeywords = listOf(
            "accesso", "login", "multicanale", "otp", "codice", "token",
            "sicurezza", "dispositivo", "autorizzazione", "alert", "attenzione",
            "credenziali", "operazione", "sconto", "sconti", "offerta", "offerte",
            "promo", "coupon", "ribasso", "telegram", "canale", "scopri",
            "risparmia", "non perderti", "saldi", "affare", "lampo", "messaggio"
        )

        // Se non è sicura (né spesa né entrata chiara) e contiene spam, bloccala
        if (!isSafeExpense && !isSafeIncome && ignoredKeywords.any { combinedText.contains(it) }) return false

        // 3. FILTRO ANTI-VUOTO
        if (!amount.any { it.isDigit() }) return false

        // 4. PASSAGGIO NELLA LAVATRICE
        val cleanMerchant = sanitizeMerchantName(merchant)

        // 5. FILTRO ANTI-DOPPIONI (60 secondi)
        val now = Clock.System.now().toEpochMilliseconds()
        val stringaDiControllo = "${cleanMerchant.replace(" ", "").lowercase()}_${amount.replace(" ", "")}"
        val currentHash = stringaDiControllo.hashCode()

        println("Monetix_Filtro: 🔍 Analizzo: [$stringaDiControllo] -> Hash Attuale: $currentHash | Hash Precedente: $lastProcessedHash | Tempo passato: ${now - lastProcessedTimeMs} ms")

        if (currentHash == lastProcessedHash && (now - lastProcessedTimeMs) < 60000) {
            println("Monetix_Filtro: 🛑 BLOCCATO! Questo è un doppione matematico.")
            return false
        }

        lastProcessedHash = currentHash
        lastProcessedTimeMs = now
        println("Monetix_Filtro: ✅ PASSATO! Salvato in memoria come nuova transazione.")

        // 🚀 6. LA DECISIONE FINALE SULLA TIPOLOGIA
        val finalIsIncome = when {
            isSafeIncome -> true
            isSafeExpense -> false
            else -> isIncome
        }

        pendingQueue.add(PendingNotification(id, amount, cleanMerchant, finalIsIncome))
        return true
    }

    fun sanitizeMerchantName(rawName: String): String {
        // 0. PRE-CLEANING: Ripara le parole spezzate
        var name = rawName.replace(Regex("-\\s+"), "").lowercase()

        // 🚀 1. ESTRAZIONE CECCHINO: Isola il vero esercente dalle frasi delle banche
        val extractors = listOf(" presso ", " a favore di ", " c/o ", " a mezzo pos ", " pos ")
        for (extractor in extractors) {
            val index = name.indexOf(extractor)
            if (index != -1) {
                // Taglia via tutto ciò che c'è prima di "presso" o "a favore di"
                name = name.substring(index + extractor.length).trim()
                break // Esce per non tagliare ulteriormente
            }
        }

        // Rimuove eventuali testi generici rimasti incastrati se non c'era il "presso"
        val bankNoise = listOf("nuova spesa", "pagamento effettuato", "pagamento", "autorizzazione", "hai speso", "transazione")
        for (noise in bankNoise) {
            if (name.startsWith(noise)) {
                name = name.removePrefix(noise).trim()
            }
        }

        // 2. LOGICA SPECIALE DATE E CODICI BANCARI
        name = name.replace(Regex("\\d{2}/\\d{2}/\\d{2}.*"), "") // Rimuove date stile 12/05/26 e tutto ciò che segue
        val noiseCodes = listOf(" mar ma", " sv", " mi", " rm", " to", " pos")
        for (code in noiseCodes) {
            name = name.replace(code, "")
        }

        // 3. I Giganti: se contiene il nome, restituisci la versione pulita
        val bigBrands = listOf("Lidl", "Eurospin", "Conad", "Coop", "Esselunga", "Amazon", "Netflix", "Spotify", "McDonald's", "Burger King", "Carrefour", "Q8", "Eni", "Tamoil", "IP", "Ikea", "Trenitalia")
        for (brand in bigBrands) {
            if (name.contains(brand.lowercase())) return brand
        }

        // 4. Rimuovi i prefissi dei circuiti di pagamento (POS)
        val prefixes = listOf("pagobancomat ", "sumup ", "nexi ", "satispay ", "paypal ")
        for (prefix in prefixes) {
            if (name.startsWith(prefix)) name = name.removePrefix(prefix)
        }

        // 5. Taglia via gli indirizzi (tutto ciò che viene dopo "via", "viale", ecc.)
        val addressKeywords = listOf(" via ", " viale ", " corso ", " piazza ", " p.zza ", " str. ")
        for (keyword in addressKeywords) {
            val index = name.indexOf(keyword)
            if (index != -1) name = name.substring(0, index)
        }

        // 6. Taglia via le estensioni legali (" srl", " di ", ecc.)
        val legalKeywords = listOf(" srl", " s.r.l", " spa", " s.p.a", " snc", " sas", " di ")
        for (keyword in legalKeywords) {
            val index = name.indexOf(keyword)
            if (index > 2) name = name.substring(0, index)
        }

        // 7. Pulisci caratteri strani e formatta con la prima lettera maiuscola
        return name.replace(Regex("[^a-z0-9&'èéàòìù]"), " ")
            .trim()
            .replace(Regex("\\s+"), " ")
            .split(" ")
            .joinToString(" ") { word ->
                word.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
            }
    }

    // 🚀 NUOVA FUNZIONE: Cancella una notifica specifica e avvisa Android
    fun removeNotificationById(id: Int) {
        pendingQueue.removeAll { it.id == id } // La toglie dalla Campanella
        onNotificationConsumed?.invoke(id)     // La toglie dalla Tendina di Android!
    }
}